import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { useWindowSize } from "lib/hooks";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import Select from "react-select";
import { Container, Row, Col, Modal, Button } from "react-bootstrap";
import PowerBiReport from "components/powerbi/PowerBiReport";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MainNav from "components/nav/MainNav";
import Butterbar from "components/butterbar/Butterbar";
import AccountAnalysisPermissionModalBody from "./AccountAnalysisPermissionModalBody";
import AccountAnalysisPageNotAllowed from "./AccountAnalysisPageNotAllowed";
import AccountAnalysisPageLoading from "./AccountAnalysisPageLoading";
import styles from "./AccountAnalysisPage.module.scss";
import { Breadcrumb, BreadcrumbData, Page } from "@avlino-inc/alenza-react";
import {
  GetAnalysisRequestType,
  Analysis,
  JWT,
  Team,
  AnalysisTeam,
  PowerBiReportType,
  PowerBiReportDropdownItemType,
  GetTeamsRequestType,
  GetAnalysisTeamsByNameRequestType,
  ErrorType,
} from "types";
import {
  decodeJwtAction,
  getAnalysisAction,
  getTeamsAction,
  getAnalysisTeamsAction,
} from "store/actions/actionCreators";
import {
  printEntireStore,
  getAnalysisReducerResponse,
  getAnalysisErrorReducerResponse,
  getTeamsReducerResponse,
  getAnalysisTeamsReducerResponse,
  getJwtReducerResponse,
} from "store/selectors";
import genStyles from "styles/General.module.scss";
import queryString from "query-string";
import * as routes from "routes";

const AccountAnalysisPage = ({
  accountName,
  analysisName,
  authed,
}: {
  accountName: string;
  analysisName: string;
  authed: Promise<boolean>;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  /* Data */
  const jwtPayloadData: JWT = useSelector((state) =>
    getJwtReducerResponse(state, "data")
  );
  const analysisData: Analysis = useSelector((state) =>
    getAnalysisReducerResponse(state, "data")
  );
  const accountTeamsData: Team[] = useSelector((state) =>
    getTeamsReducerResponse(state, "data")
  );
  const analysisTeamsData: AnalysisTeam[] = useSelector((state) =>
    getAnalysisTeamsReducerResponse(state, "data")
  );

  /* Error */
  const analysisError: ErrorType = useSelector((state) =>
    getAnalysisErrorReducerResponse(state)
  );

  useSelector((state) => printEntireStore(state));

  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [selectedDropdown, setSelectedDropdown] =
    useState<PowerBiReportDropdownItemType>();
  const [selectedReport, setSelectedReport] = useState<PowerBiReportType>();
  const [isChangingReports, setIsChangingReports] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [urlParams] = useState<AccountAnalysisPageUrlParams>(
    queryString.parse(location.search)
  );

  useEffect(() => {
    // Validate User authentication on page load. Kick them if no auth
    if (authed) {
      authed.then((allow) => {
        log.info("Allow: " + allow);
        if (allow) {
          log.info("Authenticated. Stay on page...");
        } else {
          log.info("Redirect back...");
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, history]);

  useEffect(() => {
    log.info("AccountAnalysisPage useEffect()...");
    dispatch(decodeJwtAction({}));
    const analysisRequest: GetAnalysisRequestType = {
      params: {
        account_name: accountName,
        analysis_name: analysisName,
      },
    };
    const accountTeamsRequest: GetTeamsRequestType = {
      params: {
        account_name: accountName,
      },
    };
    const analysisTeamsRequest: GetAnalysisTeamsByNameRequestType = {
      params: {
        account_name: accountName,
        analysis_name: analysisName,
      },
    };
    dispatch(getAnalysisAction(analysisRequest));
    dispatch(getTeamsAction(accountTeamsRequest));
    dispatch(getAnalysisTeamsAction(analysisTeamsRequest));
  }, [dispatch, accountName, analysisName]);

  useEffect(() => {
    // Set the appropriate state variables
    // On initial load, wait for Power BI Workspace Reports data in Redux
    // Set initial selected Report ONLY if there is not already a selected Report
    if (analysisData && analysisData.reports.length && !selectedReport) {
      let selectedReport: PowerBiReportType | undefined =
        analysisData.reports[0];
      if (urlParams.report_id) {
        selectedReport = analysisData.reports.find(
          (report) => report.id == urlParams.report_id
        );
      } else if (urlParams.report_name) {
        selectedReport = analysisData.reports.find(
          (report) => report.name == urlParams.report_name
        );
      }
      if (selectedReport) {
        setSelectedDropdown({
          id: 0,
          label: selectedReport.name,
          value: selectedReport,
          report: selectedReport,
        });
        setSelectedReport(selectedReport);
        setIsInitialLoading(false);
      }
    } else {
      log.warn("User does not have permissions to access this Analysis.");
      setIsInitialLoading(false);
    }
  }, [analysisData, selectedReport]);

  useEffect(() => {
    setIsChangingReports(false);
  }, [selectedReport]);

  /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);

  const generateReportDropdownItems = (reports: PowerBiReportType[]) => {
    if (reports) {
      return reports.map((report, index) => {
        const reportDropdownItem: PowerBiReportDropdownItemType = {
          id: index,
          label: report.name,
          value: report,
          report: report,
        };
        return reportDropdownItem;
      });
    }
  };

  const onSelectReportFromDropdown = (reportItem: any) => {
    if (reportItem) {
      const clickedReport: PowerBiReportDropdownItemType = reportItem;
      setIsChangingReports(true);
      log.info(
        "Select Power BI Report: Power BI Report Dropdown Item, Selected! : %o",
        clickedReport
      );
      // Set the Selected Report
      log.info("Setting 'selectedReport': %o", clickedReport.report);
      setSelectedDropdown(clickedReport);
      setSelectedReport(clickedReport.report);
      log.info("Current 'selectedReport': %o", selectedReport);
    }
  };

  const createSelector = (analysis: Analysis) => {
    let reportsCountDisplay: string = "0 Reports";
    if (analysis && analysis.reports.length) {
      if (analysis.reports.length == 1) {
        reportsCountDisplay = "1 Report";
      } else {
        reportsCountDisplay = `${analysis.reports.length} Reports`;
      }
    }
    return (
      <>
        {analysis && analysis.reports.length ? (
          <>
            <div className={styles["content__dropdown__cta"]}>
              <span>Select a Report:</span>
            </div>
            <Select
              className={styles["content__dropdown"]}
              options={generateReportDropdownItems(analysisData.reports)}
              defaultValue={{
                id: 0,
                label: analysisData.reports[0].name,
                value: analysisData.reports[0],
                report: analysisData.reports[0],
              }}
              placeholder={"Select Report..."}
              isClearable={false}
              isLoading={!selectedReport}
              onChange={onSelectReportFromDropdown}
              isOptionDisabled={(option) =>
                selectedDropdown ? option.id == selectedDropdown.id : false
              }
            />
            <div className={styles["content__dropdown__count"]}>
              <span>{reportsCountDisplay} available</span>
            </div>
          </>
        ) : null}
      </>
    );
  };

  const permissionsModal = (
    analysis: Analysis,
    accountTeams: Team[],
    analysisTeams: AnalysisTeam[]
  ) => {
    return (
      <div id="AccountAnalysisPermissionModal">
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showModal}
          onHide={() => {
            setShowModal(false);
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Manage Access to Analysis
              <FontAwesomeIcon
                style={{ marginLeft: "5px" }}
                icon={["fal", "eye"]}
              />
            </Modal.Title>
          </Modal.Header>
          <>
            <div style={{ marginTop: "15px", textAlign: "center" }}>
              <p style={{ fontStyle: "italic" }}>
                Choose which Teams can view this Analysis
              </p>
            </div>
            <AccountAnalysisPermissionModalBody
              analysis={analysis}
              accountTeams={accountTeams}
              analysisPermittedTeams={analysisTeams}
            />
          </>
        </Modal>
      </div>
    );
  };

  const breadcrumbItems = useMemo<BreadcrumbData[]>(
    () => [
      {
        url: routes.DASHBOARD_MAIN,
        label: "Command Center",
      },
      {
        url: routes.ACCOUNTS,
        label: "Accounts",
      },
      {
        url: routes.ACCOUNT(accountName),
        label: `${accountName}`,
      },
      {
        url: routes.ACCOUNT(accountName),
        label: "Analyses",
      },
      {
        url: routes.ACCOUNT_ANALYSIS(accountName, analysisName),
        label: `${analysisName}`,
      },
    ],
    []
  );

  const powerBiReport = useMemo(
    () => (
      <PowerBiReport
        embedToken={selectedReport && selectedReport.embedTokenResponse.token}
        embedUrl={selectedReport && selectedReport.embedUrl}
        reportId={selectedReport && selectedReport.id}
        isEmbedded={urlParams.embed ? true : false}
        isWithReportSelectDropdown={
          !urlParams.report_id && !urlParams.report_name ? true : false
        }
      />
    ),
    [selectedReport]
  );

  return (
    <div id="AccountAnalysisPage" style={{ height: "100%" }}>
      {!urlParams.embed ? (
        <MainNav
          first_name={jwtPayloadData && jwtPayloadData.first_name}
          last_name={jwtPayloadData && jwtPayloadData.last_name}
          username={jwtPayloadData && jwtPayloadData.username}
          email={jwtPayloadData && jwtPayloadData.email}
        />
      ) : null}
      {/* User has access & Report is Loading */}
      {!selectedReport && analysisData ? <Butterbar /> : null}
      <main className={styles["content"]}>
        <Container
          fluid={true}
          className={styles["content__container"]}
          style={{ minWidth: "100%", margin: "15px" }}
        >
          {!urlParams.embed ? (
            <Page.Header>
              <Breadcrumb items={breadcrumbItems} onClickItem={history.push} />
            </Page.Header>
          ) : null}
          <Row className="justify-content-md-center" style={{ height: "100%" }}>
            {/* Power BI Report Utilities */}
            {analysisData && !urlParams.report_id && !urlParams.report_name ? (
              <Col xs={12} sm={12} md={2} lg={2} xl={2}>
                <Row style={{ height: "25%" }}>
                  <Col xs={12}>
                    {/* Reports Dropdown */}
                    {createSelector(analysisData)}
                    {!urlParams.embed ? (
                      <input
                        type="submit"
                        value="Manage Access"
                        className={[
                          genStyles["alenza__btn-action"],
                          styles["content__manage-btn"],
                        ].join(" ")}
                        onClick={() => {
                          showModal ? setShowModal(false) : setShowModal(true);
                        }}
                      />
                    ) : null}
                  </Col>
                </Row>
              </Col>
            ) : null}
            {/* Power BI Report IFrame */}
            {analysisData && !isChangingReports
              ? powerBiReport
              : [
                  analysisError && analysisError.message ? (
                    <AccountAnalysisPageNotAllowed
                      user={jwtPayloadData}
                      accountName={accountName}
                      analysisName={analysisName}
                      message={analysisError.message}
                    />
                  ) : (
                    <AccountAnalysisPageLoading
                      accountName={accountName}
                      analysisName={analysisName}
                    />
                  ),
                ]}
          </Row>
        </Container>
        {showModal
          ? permissionsModal(analysisData, accountTeamsData, analysisTeamsData)
          : null}
      </main>
    </div>
  );
};

export default AccountAnalysisPage;
